import React, { useEffect, useRef, useState } from "react";

import Window from "../../contexts/Window";
import {
  useUserAddresses,
  createUserAddress,
  updateUserAddress,
  deleteUserAddress,
  setDefaultUserAddress,
} from "../../hooks/useUserAddresses";

import "./address-book.scss";

/**
 * A user's saved addresses.
 *
 * `users.address_id` is the DEFAULT address — one entry of this list, marked by
 * the radio — not a separate record, so everything that reads `$user->address`
 * (the Xero contact, shipping, the P&L's seller_country) keeps working.
 *
 * Billing and shipping live in the SAME list; there is no address type. A user
 * has as many sets as they need and one of them is the default.
 *
 * Every field AUTO-SAVES. There is no Save button anywhere in this component —
 * typing into any field persists it shortly after you stop, and leaving the
 * field flushes immediately. Each row reports its own state so "saved" is never
 * something you have to assume.
 */

const AUTOSAVE_MS = 700;

const EMPTY = {
  label: "", line1: "", line2: "", locality: "", region: "", postal_code: "", country_id: "",
};

const toForm = (a) => ({
  label: a?.label ?? "",
  line1: a?.line1 ?? "",
  line2: a?.line2 ?? "",
  locality: a?.locality ?? "",
  region: a?.region ?? "",
  postal_code: a?.postal_code ?? "",
  country_id: a?.country_id ?? "",
});

const payloadOf = (form) => ({ ...form, country_id: Number(form.country_id) });

const AddressBook = ({ userId }) => {
  const { data: addresses = [], isLoading, refetch } = useUserAddresses(userId);
  const [drafts, setDrafts] = useState([]);       // client-side rows not yet created
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!userId) return null;

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await refetch();
      setConfirmDelete(null);
    } catch (e) {
      setErr(e?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const addDraft = () => setDrafts((d) => [...d, { key: `draft-${Date.now()}` }]);
  const dropDraft = (key) => setDrafts((d) => d.filter((x) => x.key !== key));

  return (
    <div className="address-book">
      <div className="address-book__head">
        <div className="address-book__title">Addresses</div>
        <button type="button" className="address-book__add" onClick={addDraft} disabled={busy}>
          + Add address
        </button>
      </div>

      {isLoading && <div className="address-book__empty">Loading…</div>}
      {!isLoading && addresses.length === 0 && drafts.length === 0 && (
        <div className="address-book__empty">No addresses saved yet.</div>
      )}

      <ul className="address-book__list">
        {addresses.map((a) => (
          <li key={a.id} className="address-book__item">
            <label className="address-book__default" title="Set as default address">
              <input
                type="radio"
                name={`default-address-${userId}`}
                checked={!!a.is_default}
                disabled={busy}
                onChange={() => run(() => setDefaultUserAddress(userId, a.id))}
              />
            </label>
            <div className="address-book__body">
              <AddressFields
                userId={userId}
                address={a}
                onPersisted={refetch}
              />
            </div>
            <div className="address-book__actions">
              <button type="button" title="Delete" onClick={() => setConfirmDelete(a)} disabled={busy}>🗑</button>
            </div>
          </li>
        ))}

        {drafts.map((d) => (
          <li key={d.key} className="address-book__item address-book__item--new">
            <div className="address-book__body">
              <AddressFields
                userId={userId}
                address={null}
                existingIds={addresses.map((a) => a.id)}
                onPersisted={refetch}
                onCreated={() => dropDraft(d.key)}
              />
            </div>
            <div className="address-book__actions">
              <button type="button" title="Discard" onClick={() => dropDraft(d.key)}>✕</button>
            </div>
          </li>
        ))}
      </ul>

      {err && <div className="address-book__err">{err}</div>}

      {confirmDelete && (
        <div className="address-book__confirm">
          <div>
            Delete <b>{confirmDelete.label || "this address"}</b>?
            {confirmDelete.is_default && addresses.length > 1 && (
              <> Another address will become the default.</>
            )}
            {addresses.length === 1 && (
              <> This is the only address on file — the user will be left without a default.</>
            )}
          </div>
          <div className="address-book__confirm-actions">
            <button type="button" onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="address-book__danger"
              disabled={busy}
              onClick={() => run(() => deleteUserAddress(userId, confirmDelete.id))}
            >
              {busy ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * One address, auto-saving.
 *
 * Debounced while typing, flushed on blur so tabbing away never leaves an
 * unsaved field. The row owns its own id: a brand-new address has none, so the
 * FIRST successful save is a create and everything after it is an update —
 * otherwise a fast typist would create a row per keystroke.
 *
 * `country_id` is NOT NULL on the table, so nothing can be persisted until a
 * country is picked. Rather than fail silently, the row says so.
 */
const AddressFields = ({ userId, address, existingIds = [], onPersisted, onCreated }) => {
  const countries = Window.COUNTRIES || [];
  const [form, setForm] = useState(() => toForm(address));
  const [state, setState] = useState(null); // null | saving | saved | error
  const idRef = useRef(address?.id ?? null);
  // Snapshot of the ids present before this row's first create, so the created
  // row can be identified from the list the endpoint returns.
  const knownIds = useRef(existingIds);
  const timer = useRef(null);
  // Guards against two saves overlapping — the debounce can fire while a blur
  // flush is still in flight, and the second would race the first's create.
  const inFlight = useRef(false);
  const pending = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const persist = async (next) => {
    if (!next.country_id) return;
    if (inFlight.current) { pending.current = next; return; }

    inFlight.current = true;
    setState("saving");
    try {
      if (idRef.current) {
        await updateUserAddress(userId, idRef.current, payloadOf(next));
      } else {
        // The endpoint returns the whole list, not the created row, so identify
        // the new id by what wasn't there before rather than assuming order.
        const before = new Set(knownIds.current);
        const after = await createUserAddress(userId, payloadOf(next));
        const created = after.find((a) => !before.has(a.id));
        idRef.current = created?.id ?? null;
        onCreated?.();
      }
      setState("saved");
      onPersisted?.();
    } catch (e) {
      setState("error");
    } finally {
      inFlight.current = false;
      const queued = pending.current;
      pending.current = null;
      if (queued) persist(queued);
    }
  };

  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    setForm(next);
    setState(next.country_id ? "saving" : null);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), AUTOSAVE_MS);
  };

  // Leaving a field commits it immediately rather than waiting out the debounce.
  const flush = () => {
    clearTimeout(timer.current);
    persist(form);
  };

  const status =
    !form.country_id ? "Pick a country to start saving"
      : state === "saving" ? "Saving…"
      : state === "saved" ? "Saved"
      : state === "error" ? "Couldn’t save — edit a field to retry"
      : "";

  return (
    <div className="address-form">
      <div className="address-form__row">
        <input placeholder="Label (e.g. Office)" value={form.label} onChange={set("label")} onBlur={flush} />
        <select value={form.country_id} onChange={set("country_id")} onBlur={flush}>
          <option value="" disabled>— Country —</option>
          {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="address-form__row">
        <input placeholder="Address" value={form.line1} onChange={set("line1")} onBlur={flush} />
        <input placeholder="Address - line 2" value={form.line2} onChange={set("line2")} onBlur={flush} />
      </div>
      <div className="address-form__row">
        <input placeholder="Postal code" value={form.postal_code} onChange={set("postal_code")} onBlur={flush} />
        <input placeholder="City" value={form.locality} onChange={set("locality")} onBlur={flush} />
      </div>
      <div className="address-form__row">
        <input placeholder="State / Region" value={form.region} onChange={set("region")} onBlur={flush} />
        <span />
      </div>
      <div className={`address-form__status address-form__status--${state || "idle"}`}>{status}</div>
    </div>
  );
};

export default AddressBook;
